import type { CSSProperties } from "react";
import { SxProps, Theme } from "@mui/material";
import colors from "../constants/color";
import { textSM } from "../constants/textStyles";
import { getWiserTheme } from "./wiserTheme";
import { PriorityParameterGuids } from "../../constants/parameterGuids";
import { InterviewResultParameterGuids } from "../../constants/interviewResultParameterGuids";

export { getWiserTheme };

export const getBreadcrumbLinkSx = (darkModeActive: boolean): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
    color: darkModeActive ? t.textPrimary : t.textMuted,
    "&:hover": {
      color: darkModeActive ? t.textPrimary : t.textSecondary,
    },
  };
};

const reportFilterSectionBaseSx = {
  px: 0,
  py: 2,
  mb: 4,
} as const;

export const getReportFilterSectionSx = (
  extra?: SxProps<Theme>,
): SxProps<Theme> => {
  if (!extra) {
    return reportFilterSectionBaseSx;
  }
  return [reportFilterSectionBaseSx, extra] as SxProps<Theme>;
};

/** SearchInput — filtre trigger’ları ile aynı görünüm (border, yükseklik, radius) */
export const getSearchInputCssVars = (
  darkModeActive: boolean,
): Record<string, string> => {
  const t = getWiserTheme(darkModeActive);
  const border = getFilterTriggerBorder(darkModeActive, {
    open: false,
    disabled: false,
    showBorder: true,
  });
  const borderColor = border.replace(/^[\dpx\s]+solid\s/, "");
  return {
    "--search-border": borderColor,
    "--search-focus-border": darkModeActive ? t.accent : "#0052CC",
    "--search-bg": darkModeActive ? t.inputBg : "#F4F5F7",
    "--search-color": t.textPrimary,
    "--search-placeholder": t.textMuted,
  };
};

/** SearchInput inline style — RejectReasonsList vb. legacy kullanımlar */
export const getSearchInputStyle = (
  darkModeActive: boolean,
): CSSProperties => {
  const t = getWiserTheme(darkModeActive);
  return {
    minWidth: 240,
    maxWidth: 320,
    height: 40,
    background: darkModeActive ? t.inputBg : "#F4F5F7",
    borderRadius: 6,
    border: darkModeActive ? "none" : `1px solid ${t.borderStrong}`,
  };
};

export const getOutlinedFilterInputSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    "& .MuiOutlinedInput-root": {
      background: t.inputBg,
      borderRadius: "4px",
      fontFamily: "Inter, sans-serif",
      "& fieldset": {
        borderColor: darkModeActive ? "transparent" : t.borderStrong,
      },
      "&:hover fieldset": {
        borderColor: darkModeActive ? "transparent" : t.textMuted,
      },
      "&.Mui-focused fieldset": {
        borderColor: t.accent,
        borderWidth: "1px",
      },
      "& .MuiOutlinedInput-input": {
        color: t.textPrimary,
        "&::placeholder": {
          color: t.textMuted,
          opacity: 1,
        },
      },
    },
  };
};

export const getDropdownPanelSx = (darkModeActive: boolean): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    boxShadow: darkModeActive ? t.surfaceShadow : 2,
    border: `1px solid ${t.border}`,
    borderRadius: darkModeActive ? "10px" : "3px",
    background: darkModeActive ? t.surfaceStrong : t.surface,
    color: t.textPrimary,
  };
};

export const getCellTextColor = (darkModeActive: boolean): string =>
  getWiserTheme(darkModeActive).textPrimary;

export const getCellLinkColor = (darkModeActive: boolean): string =>
  getWiserTheme(darkModeActive).link;

export const getTableHeaderTextColor = (darkModeActive: boolean): string => {
  const t = getWiserTheme(darkModeActive);
  return darkModeActive ? t.textMuted : t.textSecondary;
};

export const getCellLinkStyles = (
  darkModeActive: boolean,
): SxProps<Theme> => ({
  color: getCellLinkColor(darkModeActive),
});

export const getPageTitleSx = (darkModeActive: boolean): SxProps<Theme> => ({
  fontFamily: "Inter, sans-serif",
  fontWeight: 600,
  fontSize: "20px",
  lineHeight: "28px",
  color: getWiserTheme(darkModeActive).textPrimary,
});

export const getBodyTextSx = (
  darkModeActive: boolean,
  weight = 400,
): SxProps<Theme> => ({
  fontFamily: "Inter, sans-serif",
  fontWeight: weight,
  fontSize: "14px",
  lineHeight: "20px",
  color: getWiserTheme(darkModeActive).textSecondary,
});

/** XCard / CandidateDetailsCard “Yeni Ekle” — ikincil (sayfa başına tek dolu turuncu kuralı) */
export const getCardAddButtonSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  if (darkModeActive) {
    return {
      textTransform: "none",
      fontSize: "12px",
      minWidth: "auto",
      padding: "4px 8px",
      border: `1px solid ${t.ctaPrimary}`,
      color: t.ctaPrimary,
      backgroundColor: "transparent",
      "& .MuiSvgIcon-root": {
        color: t.ctaPrimary,
      },
      "&:hover": {
        backgroundColor: "rgba(240, 133, 45, 0.12)",
        borderColor: t.ctaPrimary,
        color: t.ctaPrimary,
      },
    };
  }
  return {
    textTransform: "none",
    fontSize: "12px",
    minWidth: "auto",
    padding: "4px 8px",
    border: `1px solid ${colors.secondary}`,
    color: colors.secondary,
    backgroundColor: colors.neutral.N0,
    "&:hover": {
      backgroundColor: colors.neutral.N0,
    },
  };
};

/** Modal form alan etiketi */
export const getModalFieldLabelSx = (
  darkModeActive: boolean,
): SxProps<Theme> => ({
  mb: 0.5,
  fontWeight: 500,
  color: darkModeActive ? "#FFFFFF" : "#374151",
  fontFamily: "Inter, sans-serif",
});

/** Durum formu / modal TextField & Autocomplete input */
export const getStatusFormInputSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const borderColor = darkModeActive ? "#FFFFFF" : "#DFE1E6";
  const inputColor = darkModeActive ? "#FFFFFF" : t.textPrimary;
  return {
    fontFamily: "Inter, sans-serif",
    "& .MuiOutlinedInput-root": {
      backgroundColor: darkModeActive ? t.inputBg : "#F9FAFB",
      color: inputColor,
      "& fieldset": {
        borderColor,
      },
      "&:hover fieldset": {
        borderColor: darkModeActive ? "#FFFFFF" : t.textMuted,
      },
      "&.Mui-focused fieldset": {
        borderColor: darkModeActive ? "#FFFFFF" : t.accent,
      },
      "& .MuiInputBase-input": {
        color: inputColor,
        "&::placeholder": {
          color: t.textMuted,
          opacity: 1,
          WebkitTextFillColor: t.textMuted,
        },
        "&.Mui-disabled": {
          WebkitTextFillColor: inputColor,
          opacity: 1,
          cursor: "default",
        },
      },
      "&.Mui-disabled": {
        backgroundColor: darkModeActive ? t.surfaceMuted : "#F9FAFB",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor,
        },
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d32f2f",
      },
      "& .MuiSvgIcon-root": {
        color: darkModeActive ? "#FFFFFF" : undefined,
      },
    },
  };
};

/** Aday profili skill/tech chip */
export const getProfileSkillChipSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    border: `1px solid ${darkModeActive ? t.borderStrong : colors.neutral.N40}`,
    bgcolor: darkModeActive ? t.inputBg : "white",
    color: darkModeActive ? t.textMuted : colors.neutral.N400,
    fontSize: 14,
    fontWeight: 500,
    height: 32,
    borderRadius: "16px",
    "& .MuiChip-label": { px: 2, py: 1 },
  };
};

const LIGHT_FILTER_BACKGROUNDS = new Set([
  "#F4F5F7",
  "#FFFFFF",
  "#F8FAFC",
  "#FAFBFC",
]);

export const resolveFilterTriggerBg = (
  backgroundColor: string,
  darkModeActive: boolean,
): string => {
  const t = getWiserTheme(darkModeActive);
  if (darkModeActive && LIGHT_FILTER_BACKGROUNDS.has(backgroundColor.toUpperCase())) {
    return t.inputBg;
  }
  return backgroundColor;
};

export const getFilterTriggerBorder = (
  darkModeActive: boolean,
  state: { open: boolean; disabled: boolean; showBorder: boolean },
): string => {
  const t = getWiserTheme(darkModeActive);
  if (state.open) return `2px solid ${darkModeActive ? t.accent : "#4C9AFF"}`;
  if (darkModeActive) return `1px solid ${t.border}`;
  if (state.disabled) {
    return state.showBorder
      ? `1px solid ${t.borderStrong}`
      : "1px solid transparent";
  }
  if (state.showBorder) {
    return `1px solid ${t.borderStrong}`;
  }
  return "1px solid transparent";
};

export const getFilterPlaceholderColor = (
  darkModeActive: boolean,
  disabled: boolean,
): string => {
  const t = getWiserTheme(darkModeActive);
  if (disabled) return t.textMuted;
  return darkModeActive ? t.textPrimary : t.textSecondary;
};

export const getFilterChipSx = (
  darkModeActive: boolean,
  disabled = false,
  options?: { compact?: boolean },
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const compact = options?.compact ?? false;
  const labelColor = disabled
    ? t.textMuted
    : darkModeActive
      ? t.textPrimary
      : "#0747A6";
  const iconColor = disabled
    ? t.textMuted
    : darkModeActive
      ? t.textPrimary
      : "#0747A6";

  return {
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    background: darkModeActive ? "rgba(107, 163, 255, 0.18)" : "#DEEBFF",
    border: "none",
    borderRadius: "2px",
    height: "20px",
    padding: "2px 2px 2px 6px",
    maxWidth: compact ? "140px" : "180px",
    flexShrink: compact ? 1 : 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    "& .MuiChip-label": {
      fontFamily: "inherit",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: 0,
      verticalAlign: "middle",
      color: labelColor,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: compact ? "110px" : "150px",
      padding: 0,
    },
    "& .MuiChip-deleteIcon": {
      color: iconColor,
      fontSize: "16px",
      marginLeft: "4px",
      marginRight: 0,
      padding: 0,
      width: "16px",
      height: "16px",
    },
  };
};

export const getFilterCountChipSx = (
  darkModeActive: boolean,
  disabled = false,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const labelColor = disabled
    ? t.textMuted
    : darkModeActive
      ? t.textPrimary
      : "#0747A6";

  return {
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    background: darkModeActive ? "rgba(107, 163, 255, 0.18)" : "#DEEBFF",
    border: "none",
    borderRadius: "2px",
    height: "20px",
    padding: "2px 2px 2px 6px",
    flexShrink: 0,
    cursor: "default",
    "& .MuiChip-label": {
      fontFamily: "inherit",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: 0,
      verticalAlign: "middle",
      color: labelColor,
      padding: 0,
    },
  };
};

export const getDialogTitleSx = (
  darkModeActive: boolean,
  py: number | string = 1.5,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: darkModeActive ? t.surfaceMuted : "#F8FAFC",
    borderBottom: `1px solid ${darkModeActive ? t.border : "#E5E7EB"}`,
    px: 3,
    py,
  };
};

export const getDialogActionsSx = (darkModeActive: boolean): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    px: 3,
    pb: 2,
    pt: 1.5,
    borderTop: `1px solid ${darkModeActive ? t.border : "#E5E7EB"}`,
    backgroundColor: darkModeActive ? t.surfaceMuted : "#F8FAFC",
    ...(darkModeActive
      ? {
          "& .MuiButton-contained:not(.MuiButton-containedError)": {
            ...getCtaButtonSx(true),
          },
        }
      : {}),
  };
};

/** Sayfa / modal ana CTA — yalnızca dark modda v2 turuncu gradient (#f0852d) */
export const getCtaButtonSx = (
  darkModeActive: boolean,
  extra: SxProps<Theme> = {},
): SxProps<Theme> => {
  if (!darkModeActive) {
    return extra;
  }
  const t = getWiserTheme(true);
  return {
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    color: "#FFFFFF",
    border: "none",
    boxShadow: "0 6px 16px rgba(240, 133, 45, 0.28)",
    background: `linear-gradient(135deg, ${t.ctaPrimary}, #e07020)`,
    "&:hover": {
      background: `linear-gradient(135deg, #e07020, ${t.ctaPrimary})`,
      boxShadow: "0 8px 20px rgba(240, 133, 45, 0.38)",
    },
    "&:disabled": {
      background: t.surfaceMuted,
      color: t.textMuted,
      boxShadow: "none",
    },
    ...extra,
  };
};

/** Modal Kaydet / Ekle — getCtaButtonSx ile aynı (yalnızca dark) */
export const getModalSaveButtonSx = (
  darkModeActive: boolean,
  extra: SxProps<Theme> = {},
): SxProps<Theme> => getCtaButtonSx(darkModeActive, extra);

/** Light mode — orijinal dolu buton stilleri (dark CTA helper'ları bunları kullanır) */
export const lightNavyButtonSx: SxProps<Theme> = {
  backgroundColor: "#374E7D",
  color: "#FFFFFF",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1E2F5F",
    boxShadow: "none",
  },
};

export const lightDeepBlueButtonSx: SxProps<Theme> = {
  backgroundColor: colors.deepBlue,
  color: "#FFFFFF",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: colors.blue.B800,
    boxShadow: "none",
  },
};

export const lightModalSaveButtonSx: SxProps<Theme> = {
  textTransform: "none",
  background: colors.blue.B800,
};

export const lightPersonnelAddButtonSx: SxProps<Theme> = {
  height: 40,
  background: "#374E7D",
  color: "#fff",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "14px",
  boxShadow: "none",
  textTransform: "none",
  px: 2,
  "&:hover": {
    background: "#2c3e5d",
    boxShadow: "none",
  },
};

export const lightPersonnelModalSaveSx: SxProps<Theme> = {
  borderRadius: 2,
  textTransform: "none",
  background: "#233876",
  minWidth: 80,
};

export const lightSecondaryButtonSx: SxProps<Theme> = {
  backgroundColor: colors.secondary,
  color: "#FFFFFF",
  textTransform: "none",
  "&:hover": {
    backgroundColor: colors.secondary,
    filter: "brightness(0.9)",
  },
  "&.Mui-disabled": {
    backgroundColor: colors.neutral.N200,
    color: "#fff",
  },
};

export const lightCompactListButtonSx: SxProps<Theme> = {
  width: 64,
  height: 36,
  padding: "8px 12px",
  borderRadius: "4px",
  backgroundColor: "#374E7D",
  boxShadow: "none",
  textTransform: "none",
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "16px",
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: "#2c3e63",
    boxShadow: "none",
  },
};

export const lightNewApplicationsPrimarySx: SxProps<Theme> = {
  boxSizing: "border-box",
  display: "inline-flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  borderRadius: "4px",
  minWidth: "auto",
  textTransform: "none",
  whiteSpace: "nowrap",
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#FFFFFF",
  backgroundColor: "#374E7D",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#2F4269",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#FFFFFF",
    backgroundColor: "#374E7D",
    opacity: 0.6,
  },
};

/** Dark'ta turuncu CTA, light'ta orijinal dolu stil */
export const withDarkCtaButton = (
  darkModeActive: boolean,
  lightSx: SxProps<Theme>,
  darkExtra: SxProps<Theme> = {},
): SxProps<Theme> =>
  darkModeActive ? getCtaButtonSx(true, darkExtra) : lightSx;

export const withDarkModalSaveButton = (
  darkModeActive: boolean,
  lightSx: SxProps<Theme>,
  darkExtra: SxProps<Theme> = {},
): SxProps<Theme> =>
  darkModeActive ? getModalSaveButtonSx(true, darkExtra) : lightSx;

export const withDarkReportListButton = (
  darkModeActive: boolean,
  lightSx: SxProps<Theme>,
  darkExtra: SxProps<Theme> = {},
): SxProps<Theme> =>
  darkModeActive ? getReportListButtonSx(true, darkExtra) : lightSx;

/** Modal Vazgeç / outlined secondary buton */
export const getModalSecondaryButtonSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const borderColor = darkModeActive ? t.borderStrong : "#374E7D";
  const textColor = darkModeActive ? t.textPrimary : "#374E7D";
  return {
    boxSizing: "border-box",
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: "4px",
    border: `1px solid ${borderColor}`,
    minWidth: "auto",
    textTransform: "none",
    boxShadow: "none",
    backgroundColor: darkModeActive ? "transparent" : "#FFFFFF",
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0%",
    color: textColor,
    "& .MuiButton-label": {
      fontFamily: "Inter",
      fontStyle: "normal",
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0%",
      color: textColor,
    },
    "&.MuiButton-root, &.MuiButton-outlined, &.MuiButton-root.MuiButton-outlined":
      {
        borderColor,
        color: textColor,
      },
    "&:hover": {
      border: `1px solid ${borderColor}`,
      borderColor,
      backgroundColor: darkModeActive ? t.hoverBg : "#F8FAFC",
      boxShadow: "none",
      "& .MuiButton-label": {
        color: textColor,
      },
    },
  };
};

export const getDialogFieldSx = (darkModeActive: boolean): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    ...getOutlinedFilterInputSx(darkModeActive),
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      background: t.inputBg,
      "& .MuiInputBase-input": {
        color: t.textPrimary,
        "&::placeholder": {
          color: t.textMuted,
          opacity: 1,
        },
      },
    },
  };
};

export const getCategoryFilterChipSx = (
  darkModeActive: boolean,
  selected: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  if (!darkModeActive) {
    return {
      backgroundColor: selected ? "#374E7D" : "transparent",
      borderColor: selected ? "#374E7D" : "#6B778C",
      border: selected ? "none" : "1px solid",
      color: selected ? "#FFFFFF" : "#6B778C",
      fontWeight: selected ? 600 : 400,
      "&:hover": selected
        ? { backgroundColor: "#374E7D" }
        : {
            backgroundColor: "rgba(0, 87, 184, 0.08)",
            borderColor: "#374E7D",
          },
    };
  }

  return {
    backgroundColor: selected ? "rgba(107, 163, 255, 0.28)" : "transparent",
    border: selected
      ? `2px solid ${t.accent}`
      : `1px solid rgba(255, 255, 255, 0.35)`,
    color: selected ? t.textPrimary : t.textMuted,
    fontWeight: selected ? 600 : 400,
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: selected
        ? "rgba(107, 163, 255, 0.35)"
        : t.hoverBg,
      borderColor: selected ? t.accent : "rgba(255, 255, 255, 0.5)",
    },
  };
};

/** Pozisyon detay / form alanları — outlined input (AutocompleteField, SelectField vb.) */
export const getPositionFormInputSx = (
  darkModeActive: boolean,
  disabled = false,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const bg = disabled
    ? darkModeActive
      ? t.surfaceMuted
      : "#F9FAFB"
    : t.inputBg;
  const borderColor = darkModeActive ? t.borderStrong : "#D1D5DB";
  return {
    "& .MuiOutlinedInput-root": {
      minHeight: "40px",
      background: bg,
      border: `1px solid ${borderColor}`,
      borderRadius: "6px",
      fontSize: "14px",
      fontFamily: "Inter",
      color: t.textPrimary,
      "&:hover": {
        borderColor: !disabled
          ? darkModeActive
            ? t.textMuted
            : "#9CA3AF"
          : borderColor,
      },
      "&.Mui-focused": {
        borderColor: t.accent,
        boxShadow: darkModeActive
          ? "none"
          : "0 0 0 3px rgba(59, 130, 246, 0.1)",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    "& .MuiInputBase-input": {
      padding: "8px 12px !important",
      fontSize: "14px",
      fontFamily: "Inter",
      color: t.textPrimary,
      height: "auto !important",
    },
    "& .MuiInputBase-input::placeholder": {
      color: t.textMuted,
      opacity: 1,
      fontSize: "14px",
      fontFamily: "Inter",
    },
    "& .MuiSelect-select": {
      padding: "8px 12px",
      fontSize: "14px",
      fontFamily: "Inter",
      color: t.textPrimary,
    },
    "& .MuiAutocomplete-input": {
      padding: "8px 12px !important",
    },
    "& .MuiAutocomplete-inputRoot": {
      padding: "0 !important",
    },
  };
};

export const getTechnicGroupTriggerSx = (
  darkModeActive: boolean,
  disabled: boolean,
  open: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    position: "relative",
    height: "40px",
    background: disabled
      ? darkModeActive
        ? t.surfaceMuted
        : "#F9FAFB"
      : t.inputBg,
    borderRadius: "6px",
    border: "1px solid",
    borderColor: open ? t.accent : darkModeActive ? t.borderStrong : "#D1D5DB",
    boxShadow: open && !darkModeActive ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: "4px",
    overflow: "hidden",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "border-color 200ms ease, box-shadow 200ms ease",
    ...(!disabled && !open
      ? {
          "&:hover": {
            borderColor: darkModeActive ? t.textMuted : "#9CA3AF",
          },
        }
      : {}),
  };
};

export const getAddCandidateDatePickerSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    mb: 1,
    "& .MuiOutlinedInput-root": {
      height: "40px",
      backgroundColor: t.inputBg,
      borderRadius: "4px",
      "& fieldset": {
        borderColor: darkModeActive ? "transparent" : t.borderStrong,
      },
      "&:hover fieldset": {
        borderColor: darkModeActive ? "transparent" : "rgba(0, 0, 0, 0.87)",
      },
      "&.Mui-focused fieldset": {
        borderColor: t.accent,
        borderWidth: "1px",
      },
      "&.Mui-disabled": {
        backgroundColor: darkModeActive ? t.surfaceMuted : "rgba(0, 0, 0, 0.04)",
      },
      "& .MuiIconButton-root": {
        width: "32px !important",
        height: "32px !important",
        padding: "4px !important",
        color: t.textSecondary,
      },
      "& .MuiInputBase-input": {
        padding: "8.5px 14px",
        fontSize: "14px",
        color: t.textPrimary,
      },
    },
  };
};

/** MUI Checkbox — dropdown / filtre listelerinde görünürlük */
export const getFilterCheckboxSx = (darkModeActive: boolean): SxProps<Theme> => ({
  p: 0,
  mr: 1,
  color: "transparent",
  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
    color: "transparent",
  },
  "& .MuiSvgIcon-root": {
    fontSize: 16,
    width: 16,
    height: 16,
  },
  ...(darkModeActive
    ? {
        "&:hover": {
          backgroundColor: getWiserTheme(true).hoverBg,
          borderRadius: "2px",
        },
      }
    : {}),
});

/** DataGrid / tablo aksiyon ikonları — daire arka plan rengi */
export const getActionIconCircleFill = (darkModeActive: boolean): string =>
  darkModeActive ? getWiserTheme(true).surfaceMuted : "#F4F5F7";

/** DataGrid / tablo aksiyon ikonları — stroke rengi */
export const getActionIconStroke = (darkModeActive: boolean): string => {
  const t = getWiserTheme(darkModeActive);
  return darkModeActive ? t.link : "#103F83";
};

/** «Filtreleri Temizle» — tüm sayfalarda ortak */
export const getClearFiltersBtnSx = (
  darkModeActive: boolean,
  extra: SxProps<Theme> = {},
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    color: darkModeActive ? t.ctaPrimary : "#0065FF",
    backgroundColor: "transparent",
    textTransform: "none",
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    border: "none",
    "&:hover": {
      backgroundColor: darkModeActive ? t.navHoverBg : "rgba(0,101,255,0.08)",
    },
    ...extra,
  };
};

/** Tüm Filtreler paneli — alan etiketleri (Maaş Beklentisi, Tecrübe Yılı vb.) */
export const getFilterPanelLabelStyle = (
  darkModeActive: boolean,
): CSSProperties => {
  const t = getWiserTheme(darkModeActive);
  return {
    fontSize: 14,
    fontWeight: 400,
    fontFamily: "Inter, sans-serif",
    color: darkModeActive ? t.textPrimary : "#374e7d",
    lineHeight: "1.5",
    letterSpacing: "normal",
  };
};

/** Tüm Filtreler paneli — Min/Max sayı inputları */
export const getFilterPanelNumberInputStyle = (
  darkModeActive: boolean,
  hasError = false,
): CSSProperties => {
  const t = getWiserTheme(darkModeActive);
  return {
    width: "100%",
    height: 40,
    padding: "8px 12px",
    borderRadius: 6,
    border: hasError ? "1px solid #EF4444" : darkModeActive ? "none" : "transparent",
    background: darkModeActive ? t.inputBg : "#FFFFFF",
    outline: "none",
    color: darkModeActive ? t.textPrimary : "#42526E",
    fontSize: 14,
    fontWeight: 400,
    fontFamily: "Inter, sans-serif",
    lineHeight: "1.5",
    letterSpacing: "normal",
  };
};

/** Rapor filtreleri — Bitiş Tarihi, Şirket vb. alan etiketleri */
export const getDateLabelSx = (darkModeActive: boolean): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    color: darkModeActive ? t.textPrimary : t.textMuted,
    fontWeight: 600,
    fontSize: "12px",
    mb: 0.5,
    display: "block",
    fontFamily: "Inter",
    position: "absolute",
    top: "-20px",
    left: 0,
    whiteSpace: "nowrap",
  };
};

/** Rapor sayfaları — type="date" native input */
export const getReportDateInputSx = (darkModeActive: boolean): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    width: "100%",
    height: "40px",
    borderRadius: "3px",
    border: "none",
    backgroundColor: darkModeActive ? t.inputBg : "#F4F5F7",
    px: "14px",
    fontFamily: "Inter",
    fontSize: "14px",
    color: darkModeActive ? t.textPrimary : "#1F2937",
    cursor: "pointer",
    outline: "none",
    boxSizing: "border-box",
    colorScheme: darkModeActive ? "dark" : "light",
  };
};

/** Rapor sayfaları — «Listele» / «Yenile» (dark: turuncu CTA; light: orijinal outline) */
export const getReportListButtonSx = (
  darkModeActive: boolean,
  extra: SxProps<Theme> = {},
): SxProps<Theme> => {
  if (darkModeActive) {
    return getCtaButtonSx(true, { height: "40px", ...extra });
  }
  return {
    height: "40px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 500,
    color: "#253858",
    borderColor: "#DFE1E6",
    "&:hover": {
      borderColor: "#C1C7D0",
      backgroundColor: "#F4F5F7",
    },
    "&:disabled": {
      borderColor: "#EBECF0",
      color: "#A5ADBA",
    },
    ...extra,
  };
};

/** AccordionTable genişletilmiş satır metin stili */
export const getAccordionDetailTextStyle = (
  darkModeActive: boolean,
  fontSize = 14,
): CSSProperties => {
  const t = getWiserTheme(darkModeActive);
  return {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflow: "visible",
    textOverflow: "unset",
    display: "block",
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize,
    lineHeight: "20px",
    letterSpacing: 0,
    color: darkModeActive ? t.textPrimary : "#172B4D",
  };
};

/** Alt durum badge stili (İK GÖRÜŞME vb.) */
export const getSubStatusBadgeStyle = (
  darkModeActive: boolean,
): CSSProperties => {
  const t = getWiserTheme(darkModeActive);
  return {
    // Sabit 95px + inline-flex, "PARTNER'A YÖNLENDİRİLDİ" gibi uzun adlari
    // rozetin disina tasiriyordu. Kolon genisligini kullan, sigmazsa alt satira kir.
    maxWidth: "calc(100% - 8px)",
    display: "inline-block",
    boxSizing: "border-box",
    whiteSpace: "normal",
    overflowWrap: "break-word",
    verticalAlign: "middle",
    padding: "2px 6px",
    borderRadius: 3,
    background: darkModeActive ? "rgba(242, 113, 39, 0.22)" : "#F271271F",
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
    fontStyle: "normal",
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: 0,
    textAlign: "left",
    color: darkModeActive ? t.textSecondary : "#42526E",
  };
};

export const getKvkkStatusBackground = (
  status: string,
  darkModeActive: boolean,
): string => {
  if (darkModeActive) {
    switch (status) {
      case "Onaylandı":
        return "rgba(54, 179, 126, 0.22)";
      case "Reddedildi":
        return "rgba(242, 113, 39, 0.22)";
      case "Gönderildi":
        return "rgba(255, 171, 0, 0.22)";
      case "Gönderilmedi":
        return "rgba(255, 255, 255, 0.08)";
      default:
        return "rgba(255, 255, 255, 0.08)";
    }
  }
  switch (status) {
    case "Onaylandı":
      return "#E3FCEF";
    case "Reddedildi":
      return "#FFEBE6";
    case "Gönderildi":
      return "#FFFAE6";
    case "Gönderilmedi":
      return "#F4F5F7";
    default:
      return "#F4F5F7";
  }
};

export const getKvkkStatusTextColor = (darkModeActive: boolean): string =>
  darkModeActive ? getWiserTheme(darkModeActive).textPrimary : "#172B4D";

export const getActionIconCircleBg = (darkModeActive: boolean): string =>
  darkModeActive ? getWiserTheme(darkModeActive).hoverBg : "#F4F5F7";

export const getSearchBarGradientBorder = (darkModeActive: boolean): string =>
  darkModeActive
    ? "rgba(255, 255, 255, 0.14)"
    : "linear-gradient(90deg, rgb(25, 104, 176) 5.62%, rgb(86, 219, 83) 95.62%)";

export type TagColorPair = { bg: string; text: string };

const normalizeStatusKey = (status: string) =>
  status.toLocaleUpperCase("tr-TR");

export const getProjectStatusTagColors = (
  status: string,
  darkModeActive: boolean,
): TagColorPair => {
  const t = getWiserTheme(darkModeActive);
  const key = normalizeStatusKey(status);
  if (darkModeActive) {
    switch (key) {
      case "AKTİF":
        return { bg: "rgba(54, 179, 126, 0.22)", text: t.textPrimary };
      case "BEKLİYOR":
        return { bg: "rgba(255, 171, 0, 0.22)", text: t.textPrimary };
      case "İPTAL":
        return { bg: "rgba(242, 113, 39, 0.22)", text: t.textPrimary };
      default:
        return { bg: "rgba(255, 255, 255, 0.08)", text: t.textPrimary };
    }
  }
  switch (key) {
    case "AKTİF":
      return { bg: colors.green.G50, text: colors.green.G500 };
    case "BEKLİYOR":
      return { bg: colors.yellow.Y50, text: colors.yellow.Y500 };
    case "İPTAL":
      return { bg: colors.red.R50, text: colors.red.R500 };
    default:
      return { bg: colors.neutral.N30, text: colors.neutral.N500 };
  }
};

/**
 * Öncelik etiketi renkleri.
 *
 * Upstream `priority: number` ile 1/2/3 case'i yapıyordu. Bizde
 * PriorityParameter id'leri Guid; int case'leri sessizce HİÇ tutmaz ve her
 * etiket "belirlenmedi" rengine düşer (PORTING_PLAYBOOK §4.5, tuzak #2).
 * Otoriter kaynak: src/constants/parameterGuids.ts -> PriorityParameterGuids.
 */
export const getPriorityTagColors = (
  priority: string | null | undefined,
  darkModeActive: boolean,
): TagColorPair => {
  const t = getWiserTheme(darkModeActive);
  const id = String(priority ?? "").toLowerCase();
  if (darkModeActive) {
    switch (id) {
      case PriorityParameterGuids.high:
        return { bg: "rgba(242, 113, 39, 0.22)", text: t.textPrimary };
      case PriorityParameterGuids.medium:
        return { bg: "rgba(255, 171, 0, 0.22)", text: t.textPrimary };
      case PriorityParameterGuids.low:
        return { bg: "rgba(107, 163, 255, 0.2)", text: t.textPrimary };
      default:
        return { bg: "rgba(255, 255, 255, 0.08)", text: t.textPrimary };
    }
  }
  switch (id) {
    case PriorityParameterGuids.high:
      return { bg: colors.red.R50, text: colors.red.R500 };
    case PriorityParameterGuids.medium:
      return { bg: colors.yellow.Y50, text: colors.yellow.Y500 };
    case PriorityParameterGuids.low:
      return { bg: colors.blue.B50, text: colors.blue.B500 };
    default:
      return { bg: colors.neutral.N30, text: colors.neutral.N500 };
  }
};

export const getWorkingModelTagColors = (
  workingModel: string,
  darkModeActive: boolean,
): TagColorPair => {
  const t = getWiserTheme(darkModeActive);
  if (darkModeActive) {
    switch (workingModel) {
      case "Hybrid":
        return { bg: "rgba(107, 163, 255, 0.2)", text: t.textPrimary };
      case "Remote":
        return { bg: "rgba(147, 112, 219, 0.22)", text: t.textPrimary };
      case "Onsite":
        return { bg: "rgba(242, 113, 39, 0.18)", text: t.textPrimary };
      case "Belirsiz":
        return { bg: "rgba(255, 255, 255, 0.08)", text: t.textPrimary };
      default:
        return { bg: "rgba(147, 112, 219, 0.22)", text: t.textPrimary };
    }
  }
  switch (workingModel) {
    case "Hybrid":
      return { bg: colors.blue.B50, text: colors.neutral.N500 };
    case "Remote":
      return { bg: colors.purple.P50, text: colors.neutral.N500 };
    case "Onsite":
      return { bg: colors.red.R50, text: colors.neutral.N500 };
    case "Belirsiz":
      return { bg: colors.neutral.N30, text: colors.neutral.N500 };
    default:
      return { bg: colors.purple.P50, text: colors.neutral.N500 };
  }
};

export const getPositionModalGridSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const labelColor = darkModeActive ? t.textMuted : colors.neutral.N500;
  const valueColor = darkModeActive ? t.textPrimary : colors.neutral.N700;
  return {
    display: "grid",
    gridTemplateColumns: "1fr 3fr",
    gap: 3,
    "& > .MuiTypography-root:nth-of-type(odd)": textSM(400, labelColor),
    "& > .MuiTypography-root:nth-of-type(even)": textSM(400, valueColor),
  };
};

export const getQuestionListItemSx = (
  darkModeActive: boolean,
  checked: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: "6px",
    border: checked
      ? `1px solid ${colors.green.G400}`
      : `1px solid ${darkModeActive ? t.border : colors.neutral.N40}`,
    backgroundColor: checked
      ? darkModeActive
        ? "rgba(54, 179, 126, 0.15)"
        : colors.green.G50
      : darkModeActive
        ? t.surface
        : colors.white,
    cursor: "pointer",
  };
};

export const getQuestionListTextColor = (
  darkModeActive: boolean,
  checked: boolean,
): string => {
  const t = getWiserTheme(darkModeActive);
  if (checked) {
    return darkModeActive ? "#6dd4a8" : colors.green.G400;
  }
  return darkModeActive ? t.textPrimary : colors.blue.B200;
};

export const getInterviewStatusTagColors = (
  status: string,
  darkModeActive: boolean,
): TagColorPair => {
  const t = getWiserTheme(darkModeActive);
  const key = normalizeStatusKey(status);
  if (darkModeActive) {
    switch (key) {
      case "IK GÖRÜŞME 1":
        return { bg: "rgba(54, 179, 126, 0.22)", text: t.textPrimary };
      case "IK GÖRÜŞME 2":
        return { bg: "rgba(107, 163, 255, 0.2)", text: t.textPrimary };
      case "TEL. GÖRÜŞME":
        return { bg: "rgba(255, 171, 0, 0.22)", text: t.textPrimary };
      default:
        return { bg: "rgba(242, 113, 39, 0.22)", text: t.textPrimary };
    }
  }
  switch (key) {
    case "IK GÖRÜŞME 1":
      return { bg: colors.teal.T50, text: colors.neutral.N700 };
    case "IK GÖRÜŞME 2":
      return { bg: colors.blue.B50, text: colors.neutral.N700 };
    case "TEL. GÖRÜŞME":
      return { bg: colors.yellow.Y50, text: colors.neutral.N700 };
    default:
      return { bg: colors.red.R50, text: colors.neutral.N700 };
  }
};

/**
 * Mülakat sonucu etiketi renkleri.
 *
 * Upstream'de `resultId: number` (1 OLUMLU / 2 OLUMSUZ / 5 İPTAL) idi; bizde
 * Guid. Int case'leri sessizce hiç tutmuyordu (tuzak #2).
 * Otoriter kaynak: src/constants/interviewResultParameterGuids.ts
 * (seed: Data/Seeding/Sql/system/020_InterviewResultParameter.sql)
 */
export const getInterviewResultTagColors = (
  resultId: string | null | undefined,
  darkModeActive: boolean,
): TagColorPair => {
  const t = getWiserTheme(darkModeActive);
  const id = String(resultId ?? "").toLowerCase();
  if (darkModeActive) {
    if (id === InterviewResultParameterGuids.olumlu)
      return { bg: "rgba(54, 179, 126, 0.22)", text: t.textPrimary };
    if (
      id === InterviewResultParameterGuids.olumsuz ||
      id === InterviewResultParameterGuids.iptal
    )
      return { bg: "rgba(242, 113, 39, 0.22)", text: t.textPrimary };
    return { bg: "rgba(147, 112, 219, 0.22)", text: t.textPrimary };
  }
  if (id === InterviewResultParameterGuids.olumlu)
    return { bg: colors.green.G50, text: colors.green.G500 };
  if (
    id === InterviewResultParameterGuids.olumsuz ||
    id === InterviewResultParameterGuids.iptal
  )
    return { bg: colors.red.R50, text: colors.red.R500 };
  return { bg: colors.purple.P50, text: colors.purple.P500 };
};

export const getReusableTabsSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    "& .MuiTabs-indicator": {
      backgroundColor: darkModeActive ? t.accent : "#0747A6",
    },
    "& .MuiTabs-flexContainer": {
      borderBottom: `2px solid ${darkModeActive ? t.border : "#E2E8F0"}`,
    },
  };
};

export const getReusableTabSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    textTransform: "none",
    fontWeight: 500,
    color: darkModeActive ? t.textPrimary : "#42526E",
    "&.Mui-selected": {
      color: darkModeActive ? t.accent : "#0747A6",
    },
  };
};

export const getHrCommentCardSx = (darkModeActive: boolean): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    backgroundColor: darkModeActive
      ? t.surfaceMuted
      : `${colors.yellow.Y50}80`,
    border: `1px solid ${darkModeActive ? t.border : colors.yellow.Y100}`,
    borderRadius: "8px",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflow: "visible",
    minHeight: "18vh",
    maxHeight: "50vh",
    position: "relative",
  };
};

export const getModalDataGridSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    border: "none",
    color: darkModeActive ? t.textPrimary : undefined,
    "& .MuiDataGrid-columnHeaders": {
      color: darkModeActive ? t.textSecondary : colors.neutral.N200,
      backgroundColor: darkModeActive ? t.tableHeaderBg : "#F8FAFC",
      borderBottomColor: darkModeActive ? t.border : undefined,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      color: darkModeActive ? t.textSecondary : colors.neutral.N200,
    },
    "& .MuiDataGrid-cell": {
      color: darkModeActive ? t.textPrimary : undefined,
      borderBottomColor: darkModeActive ? t.border : undefined,
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: darkModeActive ? t.hoverBg : undefined,
    },
    "& .MuiDataGrid-virtualScroller, & .MuiDataGrid-main": {
      overflowX: "hidden",
    },
    "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within":
      {
        outline: "none",
      },
  };
};

export const getSidebarIconFilter = (darkModeActive: boolean): string =>
  darkModeActive ? "brightness(0) invert(0.82)" : "none";

/** Segmented control kapsayıcı (CV Yükle / LinkedIn URL vb.) */
export const getSegmentedControlContainerSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    display: "flex",
    backgroundColor: darkModeActive ? t.surfaceMuted : colors.neutral.N30,
    borderRadius: "8px",
    p: "4px",
    border: `1px solid ${darkModeActive ? t.border : colors.neutral.N30}`,
  };
};

export const getSegmentedControlItemSx = (
  darkModeActive: boolean,
  isActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    flex: 1,
    p: "10px",
    textAlign: "center",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: "12px",
    transition: "all 0.2s ease",
    backgroundColor: isActive
      ? darkModeActive
        ? t.surface
        : colors.neutral.N0
      : "transparent",
    color: darkModeActive ? t.textPrimary : colors.neutral.N700,
    boxShadow:
      isActive && darkModeActive ? "0 1px 3px rgba(0, 0, 0, 0.25)" : "none",
  };
};

export type TransferStepperState = {
  isActive: boolean;
  isCompleted: boolean;
  isClickable: boolean;
};

export const getTransferStepperStepSx = (
  darkModeActive: boolean,
  { isActive, isCompleted, isClickable }: TransferStepperState,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const backgroundColor = darkModeActive
    ? isCompleted
      ? "rgba(54, 179, 126, 0.18)"
      : isActive
        ? "rgba(107, 163, 255, 0.18)"
        : t.surface
    : isCompleted
      ? colors.green.G50
      : isActive
        ? colors.blue.B50
        : "#FFFFFF";

  const borderColor = darkModeActive
    ? isCompleted
      ? colors.green.G400
      : isActive
        ? t.accent
        : t.border
    : isCompleted
      ? colors.green.G400
      : isActive
        ? colors.blue.B300
        : colors.neutral.N100;

  return {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "10px",
    borderRadius: "8px",
    position: "relative",
    transition: "all 0.3s ease",
    backgroundColor,
    border: `0.5px solid ${borderColor}`,
    cursor: isClickable ? "pointer" : "default",
    "&:hover": isClickable
      ? {
          transform: "translateY(-2px)",
          boxShadow: darkModeActive
            ? "0 2px 8px rgba(0, 0, 0, 0.25)"
            : "0 2px 8px rgba(0, 0, 0, 0.1)",
        }
      : {},
  };
};

export const getTransferStepperNumberSx = (
  darkModeActive: boolean,
  { isActive, isCompleted }: Pick<TransferStepperState, "isActive" | "isCompleted">,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const borderColor = darkModeActive
    ? isCompleted
      ? colors.green.G400
      : isActive
        ? t.accent
        : t.borderStrong
    : isCompleted
      ? colors.green.G400
      : isActive
        ? colors.blue.B300
        : colors.neutral.N100;

  const color = darkModeActive
    ? isCompleted
      ? colors.green.G400
      : isActive
        ? t.accent
        : t.textMuted
    : isCompleted
      ? colors.green.G400
      : isActive
        ? colors.blue.B300
        : colors.neutral.N100;

  return {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontFamily: "Inter",
    fontWeight: 500,
    marginRight: "8px",
    flexShrink: 0,
    border: "1px solid",
    borderColor,
    color,
  };
};

export const getTransferStepperTitleSx = (
  darkModeActive: boolean,
  { isActive, isCompleted }: Pick<TransferStepperState, "isActive" | "isCompleted">,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  const color = darkModeActive
    ? isCompleted
      ? colors.green.G400
      : isActive
        ? t.accent
        : t.textPrimary
    : isCompleted
      ? colors.green.G400
      : isActive
        ? colors.blue.B300
        : colors.neutral.N100;

  return {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    marginBottom: "4px",
    color,
  };
};

export const getTransferStepperDescriptionSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    fontFamily: "Inter",
    fontSize: "12px",
    lineHeight: "16px",
    color: darkModeActive ? t.textSecondary : colors.neutral.N700,
  };
};

export const getTransferStepperLineSx = (
  darkModeActive: boolean,
): SxProps<Theme> => {
  const t = getWiserTheme(darkModeActive);
  return {
    width: "26px",
    height: "2px",
    backgroundColor: darkModeActive ? t.borderStrong : colors.neutral.N40,
    flexShrink: 0,
  };
};
